import { useEffect, useRef, useState } from "react";

const STORAGE_KEY = "mooz:sitePasswordGate:v1";
export const PASSWORD_GATE_REQUEST_EVENT = "mooz:passwordGateRequest";
const UNLOCK_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

export function requestPasswordGate(detail) {
    if (typeof window === "undefined") return;
    window.dispatchEvent(
        new CustomEvent(PASSWORD_GATE_REQUEST_EVENT, { detail: detail || {} })
    );
}

function normalizePathname(pathname) {
    if (!pathname || pathname === "/") return "/";
    return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

function hashString(input) {
    // Non-crypto, stable hash (avoids storing plaintext password in storage).
    let h = 5381;
    for (let i = 0; i < input.length; i++) {
        h = (h * 33) ^ input.charCodeAt(i);
    }
    return (h >>> 0).toString(16);
}

function readUnlocked(signature) {
    if (typeof window === "undefined") return false;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return false;
        const data = JSON.parse(raw);
        if (data?.unlocked !== true) return false;
        if (!signature) return false;
        if (data?.sig !== signature) return false;
        const unlockedAt = Number(data?.unlockedAt || 0);
        if (!unlockedAt || Number.isNaN(unlockedAt)) return false;
        return Date.now() - unlockedAt < UNLOCK_TTL_MS;
    } catch {
        return false;
    }
}

function writeUnlocked(unlocked, signature) {
    if (typeof window === "undefined") return;
    const payload = {
        version: 1,
        unlocked: unlocked === true,
        sig: signature || null,
        unlockedAt: unlocked === true ? Date.now() : null,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

export function getPasswordGateStatus(pathname) {
    // NOTE: The site-wide gate is enforced in production via a Netlify Edge Function.
    // Keeping this component as a no-op prevents accidentally shipping a client-side password.
    const gatePasswordNorm = "";
    const enabled = false;
    const signature = null;

    // Normalize for potential future route-based exemptions.
    normalizePathname(pathname);
    // Site-wide renovation gate: lock *all* routes when enabled.
    // (Password presence enables the gate.)
    const exempt = false;

    const unlocked = enabled ? readUnlocked(signature) : false;
    const locked = enabled && !exempt && !unlocked;

    return { enabled, exempt, unlocked, locked, signature, gatePasswordNorm };
}

export default function PasswordGateway({
    pathname,
    forceOpen = false,
    onUnlocked,
    onRequestClose,
    children,
}) {
    const {
        enabled,
        exempt: isExempt,
        locked,
        signature,
        gatePasswordNorm,
    } = getPasswordGateStatus(pathname);

    const [unlocked, setUnlocked] = useState(() =>
        enabled ? readUnlocked(signature) : false
    );
    const [value, setValue] = useState("");
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const didFireUnlocked = useRef(false);

    useEffect(() => {
        const shouldLockBody = enabled && !unlocked && (locked || forceOpen);
        if (!shouldLockBody) return;
        document.body.classList.add("pg-locked");
        return () => {
            document.body.classList.remove("pg-locked");
        };
    }, [enabled, unlocked, locked, forceOpen]);

    useEffect(() => {
        if (!enabled || !unlocked) return;
        writeUnlocked(true, signature);
    }, [enabled, unlocked, signature]);

    useEffect(() => {
        if (!enabled) return;
        if (!unlocked) return;
        if (didFireUnlocked.current) return;
        didFireUnlocked.current = true;
        onUnlocked?.();
    }, [enabled, unlocked, onUnlocked]);

    const shouldLock = enabled && !unlocked && (locked || forceOpen);

    if (!enabled || unlocked || (!forceOpen && isExempt)) {
        return <>{children}</>;
    }

    const onSubmit = (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setError("");
        setIsSubmitting(true);

        window.setTimeout(() => {
            if (value.trim() !== gatePasswordNorm) {
                setError("Incorrect password.");
                setIsSubmitting(false);
                return;
            }
            setUnlocked(true);
            setIsSubmitting(false);
        }, 300);
    };

    if (!shouldLock) return <>{children}</>;

    return (
        <>
            <div className="pg" role="dialog" aria-modal="true">
                <div className="pg__card">
                    {/* <p className="pg__eyebrow">Under renovation</p> */}
                    <h1 className="pg__title">
                        We’re{" "}
                        <span className="pg__highlight">
                            polishing the pixels
                        </span>
                    </h1>
                    <p className="pg__sub">
                        Mooz is under renovation while we improve performance
                        and reliability.
                        <br />
                        <br />
                        Beta user? Enter your passcode to continue.
                    </p>

                    <form className="pg__form" onSubmit={onSubmit}>
                        <input
                            id="site-password"
                            type="password"
                            className="pg__input"
                            placeholder="Enter passcode"
                            autoComplete="current-password"
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            disabled={isSubmitting}
                            autoFocus
                        />
                        {error ? (
                            <p className="pg__error" role="alert">
                                {error}
                            </p>
                        ) : null}
                        <button
                            type="submit"
                            className="pg__btn"
                            disabled={isSubmitting}
                            aria-busy={isSubmitting}
                        >
                            {isSubmitting ? (
                                <span
                                    className="pg__spinner"
                                    aria-hidden="true"
                                />
                            ) : (
                                "Unlock"
                            )}
                        </button>
                    </form>
                    <br />
                    <p className="pg__hint">
                        Need access?{" "}
                        <a
                            href="mailto:jk@joycekadibu.com?subject=Mooz%20beta%20access"
                            className="pg__link"
                        >
                            Email support
                        </a>
                    </p>
                    {forceOpen ? (
                        <button
                            type="button"
                            className="pg__btn pg__btn--ghost"
                            onClick={onRequestClose}
                        >
                            Cancel
                        </button>
                    ) : null}
                </div>
            </div>
        </>
    );
}
